import React, { useState } from 'react'
import EditIcon from '@material-ui/icons/Edit'
import { Tabs, Tab } from '@material-ui/core'

import { ERRORS } from 'consts'
import { useApi } from 'hooks'
import { snackbar } from 'components'

import { getTags } from './api'
import { DefaultTable } from '../components'
import { GetTagsResponse, Tag } from './types'
import { CreateTagDialog, DeleteTagDialog, UpdateTagDialog } from './components'
import { TAG_GROUPS } from './consts'

const removeAccents = (str: string) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '')

const columns = [
  { title: 'ID', field: 'id' },
  {
    title: 'Título',
    field: 'title',
    customFilterAndSearch: (term: string, rowData: Tag) =>
      rowData.title !== null &&
      removeAccents(rowData.title)
        .toLowerCase()
        .indexOf(removeAccents(term).toLowerCase()) !== -1,
  },
  {
    title: 'Descripción',
    field: 'description',
    customFilterAndSearch: (term: string, rowData: Tag) =>
      rowData.description !== null &&
      removeAccents(rowData.description)
        .toLowerCase()
        .indexOf(removeAccents(term).toLowerCase()) !== -1,
  },
  {
    title: 'Activo',
    render: (rowData: Tag) => (rowData.active ? 'Sí' : 'No'),
    sorting: false,
  },
]

const getTagsResponseGetter = (responseData: GetTagsResponse) => responseData?.tags ?? []

const Tags: React.FC = () => {
  const [{ data: tags, isLoading }, , setTags] = useApi<GetTagsResponse, Tag[]>(
    getTags,
    getTagsResponseGetter,
    { baseData: [], onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE) },
  )
  const [isCreateTagDialogOpen, setIsCreateTagDialogOpen] = useState(false)
  const [isDeleteTagDialogOpen, setIsDeleteTagDialogOpen] = useState(false)
  const [tagToDelete, setTagToDelete] = useState<Tag | null>(null)
  const [isUpdateTagDialogOpen, setIsUpdateTagDialogOpen] = useState(false)
  const [tagToUpdate, setTagToUpdate] = useState<Tag | null>(null)
  const [activeTab, setActiveTab] = useState(0)

  const handleTabChange = (_: React.ChangeEvent<Record<string, unknown>>, newValue: number) => {
    setActiveTab(newValue)
  }

  const csvTags = tags.filter(tag => tag.group === TAG_GROUPS.FROM_CSV)
  const otherTags = tags.filter(tag => tag.group !== TAG_GROUPS.FROM_CSV)

  const handleCreateTagDone = (newTag: Tag) => {
    setTags([...tags, newTag])
    setIsCreateTagDialogOpen(false)
  }

  const handleDeleteTagDialogClose = () => {
    setIsDeleteTagDialogOpen(false)
    setTagToDelete(null)
  }

  const handleDeleteTagDone = () => {
    if (!tagToDelete) return
    setTags(tags.filter(tag => tag.id !== tagToDelete.id))
    handleDeleteTagDialogClose()
  }

  const handleUpdateTagDialogClose = () => {
    setIsUpdateTagDialogOpen(false)
    setTagToUpdate(null)
  }

  const handleUpdateTagDone = (updatedTag: Tag) => {
    setTags(
      tags.map(tag => {
        if (tag.id === updatedTag.id) {
          return updatedTag
        }
        return tag
      }),
    )
    handleUpdateTagDialogClose()
  }

  const actions = [
    {
      icon: 'add',
      tooltip: 'Nueva etiqueta(tag)',
      isFreeAction: true,
      onClick: () => setIsCreateTagDialogOpen(true),
      hidden: activeTab !== 1, // Solo mostrar en la solapa de Tags por Carga masiva
    },
    {
      icon: () => <EditIcon />,
      tooltip: 'Editar etiqueta(tag)',
      onClick: (_: React.SyntheticEvent, row: Tag | Tag[]) => {
        setTagToUpdate(row as Tag)
        setIsUpdateTagDialogOpen(true)
      },
    },
  ]

  return (
    <>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        style={{ borderBottom: '1px solid #e0e0e0', marginBottom: '16px' }}
      >
        <Tab label="Tags" />
        <Tab label="Tags por Carga masiva" />
      </Tabs>
      <DefaultTable
        title=""
        columns={columns}
        data={activeTab === 0 ? otherTags : csvTags}
        isLoading={isLoading}
        actions={actions}
        options={{
          showFirstLastPageButtons: true,
          toolbar: true,
          actionsColumnIndex: -1,
        }}
      />
      {isCreateTagDialogOpen && (
        <CreateTagDialog
          handleClose={() => setIsCreateTagDialogOpen(false)}
          onDone={handleCreateTagDone}
        />
      )}
      {isDeleteTagDialogOpen && tagToDelete && (
        <DeleteTagDialog
          handleClose={handleDeleteTagDialogClose}
          tagId={tagToDelete.id}
          tagTitle={tagToDelete.title}
          onDone={handleDeleteTagDone}
        />
      )}
      {isUpdateTagDialogOpen && tagToUpdate && (
        <UpdateTagDialog
          handleClose={handleUpdateTagDialogClose}
          tagToUpdate={tagToUpdate}
          onDone={handleUpdateTagDone}
        />
      )}
    </>
  )
}

export { Tags }
